/**
 * useApi Hook
 *
 * Fetches data from our backend API with auth token handling.
 */

import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { warehouses as mockWarehouses } from '../data/mockData';

export function useApi() {
  const [warehouses, setWarehouses] = useState({});
  const [repairs, setRepairs] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all data from our backend
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Check if we have a token
      if (!api.hasToken()) {
        console.log('No auth token, using mock data');
        setWarehouses(mockWarehouses);
        setLoading(false);
        return;
      }

      const data = await api.get('/api/data');
      
      setWarehouses(data.warehouses || {});
      setRepairs(data.repairs || []);
      setOrganizations(data.organizations || []);

    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err);
      
      // Fallback to mock data on error
      if (err.status === 401) {
        console.log('Unauthorized, using mock data');
      }
      setWarehouses(mockWarehouses);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --- Warehouse Actions ---

  const checkWarehouseExists = async (name) => {
    try {
      const data = await api.get(`/api/warehouses/check/${encodeURIComponent(name)}`);
      return data.exists ? data.warehouse : null;
    } catch (err) {
      console.error('Error checking warehouse existence:', err);
      return null;
    }
  };

  const createWarehouse = async (name, layoutData, options = {}) => {
    try {
      const { override = false, update = false, existingWarehouseId = null } = options;

      let result;

      if (update && existingWarehouseId) {
        // Update existing warehouse
        result = await api.put(`/api/warehouses/${existingWarehouseId}`, {
          name,
          displayName: name,
          blueprint: layoutData.blueprint,
          gpsPolygon: layoutData.gps?.polygon,
          orgId: layoutData.org_id
        });
      } else if (override && existingWarehouseId) {
        // Override existing warehouse
        result = await api.put(`/api/warehouses/${existingWarehouseId}`, {
          name,
          displayName: name,
          blueprint: layoutData.blueprint,
          gpsPolygon: layoutData.gps?.polygon
        });
      } else {
        // Create new warehouse
        result = await api.post('/api/warehouses', {
          name,
          displayName: name,
          blueprint: layoutData.blueprint,
          gpsPolygon: layoutData.gps?.polygon,
          orgId: layoutData.org_id
        });

        // Create initial zone if provided
        if (layoutData.zones && layoutData.zones.length > 0) {
          const mainZone = layoutData.zones[0];
          await api.post('/api/zones', {
            warehouseId: result.id,
            name: mainZone.name,
            displayName: mainZone.name,
            rect: mainZone.rect,
            color: mainZone.color,
            borderColor: mainZone.borderColor
          });
        }
      }

      await fetchData();
      return result.id;
    } catch (err) {
      console.error('Error creating/updating warehouse:', err);
      alert('Failed to save warehouse to database.');
      return null;
    }
  };

  const updateBlueprint = async (warehouseId, blueprint) => {
    try {
      await api.patch(`/api/warehouses/${warehouseId}/blueprint`, { blueprint });
      
      // Optimistic update
      setWarehouses(prev => ({
        ...prev,
        [warehouseId]: { ...prev[warehouseId], blueprint }
      }));
    } catch (err) {
      console.error('Error updating blueprint:', err);
    }
  };

  const updateZones = async (warehouseId, zones) => {
    try {
      const formattedZones = zones.map(z => ({
        id: z.id,
        name: z.name,
        displayName: z.displayName || z.name,
        rect: z.rect,
        color: z.color,
        borderColor: z.borderColor,
        labelPos: z.labelPos
      }));

      const updatedZones = await api.put(`/api/zones/warehouse/${warehouseId}/batch`, { zones: formattedZones });

      setWarehouses(prev => ({
        ...prev,
        [warehouseId]: { ...prev[warehouseId], zones: updatedZones }
      }));
    } catch (err) {
      console.error('Error updating zones:', err);
    }
  };

  const updateWarehouseGPS = async (warehouseId, gpsPolygon) => {
    try {
      await api.patch(`/api/warehouses/${warehouseId}/gps`, { gpsPolygon });
      
      // Optimistic update
      setWarehouses(prev => ({
        ...prev,
        [warehouseId]: { 
          ...prev[warehouseId], 
          gps: { polygon: gpsPolygon }
        }
      }));
      
      return true;
    } catch (err) {
      console.error('Error updating warehouse GPS:', err);
      return false;
    }
  };

  // --- Organization Actions ---

  const createOrganization = async (orgData, options = {}) => {
    const { createWarehouse: shouldCreateWarehouse = true } = options;
    
    try {
      const result = await api.post('/api/organizations', {
        name: orgData.name,
        type: orgData.type,
        address: orgData.address,
        latitude: orgData.latitude,
        longitude: orgData.longitude,
        contactEmail: orgData.contact_email,
        contactPhone: orgData.contact_phone,
        createWarehouse: shouldCreateWarehouse && orgData.latitude && orgData.longitude
      });

      await fetchData();
      return result;
    } catch (err) {
      console.error('Error creating organization:', err);
      throw err;
    }
  };

  const updateOrganization = async (orgId, orgData) => {
    try {
      const result = await api.put(`/api/organizations/${orgId}`, {
        name: orgData.name,
        type: orgData.type,
        address: orgData.address,
        latitude: orgData.latitude,
        longitude: orgData.longitude,
        contactEmail: orgData.contact_email,
        contactPhone: orgData.contact_phone
      });

      await fetchData();
      return result;
    } catch (err) {
      console.error('Error updating organization:', err);
      throw err;
    }
  };

  const deleteOrganization = async (orgId) => {
    try {
      await api.delete(`/api/organizations/${orgId}`);
      await fetchData();
      return true;
    } catch (err) {
      console.error('Error deleting organization:', err);
      throw err;
    }
  };

  // --- Asset Actions ---

  const createAsset = async (assetData) => {
    try {
      const result = await api.post('/api/assets', {
        id: assetData.id,
        warehouseId: assetData.warehouseId,
        zoneId: assetData.zoneId || null,
        name: assetData.name,
        type: assetData.type,
        category: assetData.category || null,
        status: assetData.status || 'Active',
        position: assetData.position || null,
        imageUrl: assetData.imageUrl || null,
        capacity: assetData.capacity || null,
        images: assetData.images || [],
        brand: assetData.brand || null,
        isTemplate: assetData.isTemplate || false
      });

      await fetchData();
      return result;
    } catch (err) {
      console.error('Error creating asset:', err);
      throw err;
    }
  };

  const updateAsset = async (assetId, assetData) => {
    try {
      const result = await api.put(`/api/assets/${assetId}`, {
        warehouseId: assetData.warehouseId,
        zoneId: assetData.zoneId,
        name: assetData.name,
        type: assetData.type,
        category: assetData.category,
        status: assetData.status,
        position: assetData.position,
        imageUrl: assetData.imageUrl,
        capacity: assetData.capacity,
        images: assetData.images,
        brand: assetData.brand || null,
        isTemplate: assetData.isTemplate !== undefined ? assetData.isTemplate : null
      });

      await fetchData();
      return result;
    } catch (err) {
      console.error('Error updating asset:', err);
      throw err;
    }
  };

  // Update asset capacity
  const updateAssetCapacity = async (assetId, capacity) => {
    try {
      const result = await api.patch(`/api/assets/${assetId}/capacity`, { capacity });
      
      // Optimistic update
      setWarehouses(prev => {
        const updated = { ...prev };
        for (const whId in updated) {
          const assets = updated[whId].assets || [];
          const assetIndex = assets.findIndex(a => a.id === assetId);
          if (assetIndex !== -1) {
            const newAssets = [...assets];
            newAssets[assetIndex] = { ...newAssets[assetIndex], capacity };
            updated[whId] = { ...updated[whId], assets: newAssets };
            break;
          }
        }
        return { ...updated };
      });

      return result;
    } catch (err) {
      console.error('Error updating asset capacity:', err);
      throw err;
    }
  };

  // Scrap an asset
  const scrapAsset = async (assetId, reason = null) => {
    try {
      const result = await api.patch(`/api/assets/${assetId}/scrap`, { reason });
      
      // Update local state
      setWarehouses(prev => {
        const updated = { ...prev };
        for (const whId in updated) {
          const assets = updated[whId].assets || [];
          const assetIndex = assets.findIndex(a => a.id === assetId);
          if (assetIndex !== -1) {
            const newAssets = [...assets];
            newAssets[assetIndex] = { 
              ...newAssets[assetIndex], 
              status: 'Scrapped',
              scrapped_at: new Date().toISOString()
            };
            updated[whId] = { ...updated[whId], assets: newAssets };
            break;
          }
        }
        return { ...updated };
      });

      await fetchData(); // Refresh to get updated repairs
      return result;
    } catch (err) {
      console.error('Error scrapping asset:', err);
      throw err;
    }
  };

  // Add images to an asset
  const addAssetImages = async (assetId, imageIds) => {
    try {
      const result = await api.post(`/api/assets/${assetId}/images`, { imageIds });
      await fetchData();
      return result;
    } catch (err) {
      console.error('Error adding asset images:', err);
      throw err;
    }
  };

  // Remove an image from an asset
  const removeAssetImage = async (assetId, imageId) => {
    try {
      const result = await api.delete(`/api/assets/${assetId}/images/${imageId}`);
      await fetchData();
      return result;
    } catch (err) {
      console.error('Error removing asset image:', err);
      throw err;
    }
  };

  const deleteAsset = async (assetId) => {
    try {
      await api.delete(`/api/assets/${assetId}`);
      await fetchData();
      return true;
    } catch (err) {
      console.error('Error deleting asset:', err);
      throw err;
    }
  };

  // Assign fiducial marker to asset (for expendable assets)
  const assignFiducialMarker = async (assetId) => {
    try {
      const result = await api.post(`/api/assets/${assetId}/assign-marker`);
      await fetchData();
      return result;
    } catch (err) {
      console.error('Error assigning fiducial marker:', err);
      throw err;
    }
  };

  // Release fiducial marker from asset (for recycling)
  const releaseFiducialMarker = async (assetId) => {
    try {
      const result = await api.post(`/api/assets/${assetId}/release-marker`);
      await fetchData();
      return result;
    } catch (err) {
      console.error('Error releasing fiducial marker:', err);
      throw err;
    }
  };

  const updateAssetPosition = async (assetId, position, zoneId = undefined) => {
    // Optimistic update FIRST for immediate visual feedback
    let originalAsset = null;
    let originalWarehouseId = null;
    
    setWarehouses(prev => {
      const updated = { ...prev };
      for (const whId in updated) {
        const assets = updated[whId].assets || [];
        const assetIndex = assets.findIndex(a => a.id === assetId);
        if (assetIndex !== -1) {
          // Store original for rollback on error
          originalAsset = { ...assets[assetIndex] };
          originalWarehouseId = whId;
          
          // Create completely new objects at every level for React to detect changes
          const newAssets = [...assets];
          newAssets[assetIndex] = { 
            ...newAssets[assetIndex], 
            position: { x: position.x, y: position.y }, // New position object
            ...(zoneId !== undefined ? { zoneId } : {})
          };
          updated[whId] = { 
            ...updated[whId], 
            assets: newAssets // New assets array
          };
          break;
        }
      }
      return { ...updated }; // Return new warehouses object
    });

    try {
      const result = await api.patch(`/api/assets/${assetId}/position`, {
        position,
        zoneId
      });

      // Update with server response to ensure consistency
      setWarehouses(prev => {
        const updated = { ...prev };
        for (const whId in updated) {
          const assets = updated[whId].assets || [];
          const assetIndex = assets.findIndex(a => a.id === assetId);
          if (assetIndex !== -1) {
            const newAssets = [...assets];
            // Use server response if available, otherwise keep optimistic update
            const serverPosition = result.position || position;
            newAssets[assetIndex] = { 
              ...newAssets[assetIndex], 
              position: typeof serverPosition === 'object' ? serverPosition : { x: position.x, y: position.y },
              ...(zoneId !== undefined ? { zoneId } : {})
            };
            updated[whId] = { ...updated[whId], assets: newAssets };
            break;
          }
        }
        return { ...updated };
      });

      return result;
    } catch (err) {
      console.error('Error updating asset position:', err);
      
      // Rollback optimistic update on error
      if (originalAsset && originalWarehouseId) {
        setWarehouses(prev => {
          const updated = { ...prev };
          if (updated[originalWarehouseId]) {
            const assets = updated[originalWarehouseId].assets || [];
            const assetIndex = assets.findIndex(a => a.id === assetId);
            if (assetIndex !== -1) {
              const newAssets = [...assets];
              newAssets[assetIndex] = originalAsset;
              updated[originalWarehouseId] = { 
                ...updated[originalWarehouseId], 
                assets: newAssets 
              };
            }
          }
          return { ...updated };
        });
      }
      
      throw err;
    }
  };

  // --- Beacon Actions ---

  const getBeacons = async (warehouseId = null) => {
    try {
      const endpoint = warehouseId 
        ? `/api/beacons/warehouse/${warehouseId}`
        : '/api/beacons';
      const result = await api.get(endpoint);
      return result;
    } catch (err) {
      console.error('Error getting beacons:', err);
      throw err;
    }
  };

  const createBeacon = async (beaconData) => {
    try {
      const result = await api.post('/api/beacons', {
        name: beaconData.name,
        macAddress: beaconData.macAddress || null,
        warehouseId: beaconData.warehouseId,
        position: beaconData.position,
        gpsPosition: beaconData.gpsPosition || null,
        status: beaconData.status || 'active',
        config: beaconData.config || {}
      });
      return result;
    } catch (err) {
      console.error('Error creating beacon:', err);
      throw err;
    }
  };

  const updateBeacon = async (beaconId, beaconData) => {
    try {
      const result = await api.put(`/api/beacons/${beaconId}`, beaconData);
      return result;
    } catch (err) {
      console.error('Error updating beacon:', err);
      throw err;
    }
  };

  const updateBeaconPosition = async (beaconId, position) => {
    try {
      const result = await api.patch(`/api/beacons/${beaconId}/position`, { position });
      return result;
    } catch (err) {
      console.error('Error updating beacon position:', err);
      throw err;
    }
  };

  const deleteBeacon = async (beaconId) => {
    try {
      await api.delete(`/api/beacons/${beaconId}`);
      return true;
    } catch (err) {
      console.error('Error deleting beacon:', err);
      throw err;
    }
  };

  // ============================================
  // HUB ACTIONS (ESP32 Gateway Devices)
  // ============================================
  // IMPORTANT: Hubs are ESP32 gateway devices (Gateway_K, Gateway_L, etc.)
  // They are stored in the hubs table and identified by hub_id.
  // Beacons are BLE tags (MAC identifiers) attached to assets.

  /**
   * Get all hubs (optionally filtered by warehouse)
   * @param {string|null} warehouseId - Optional warehouse ID to filter hubs
   */
  const getHubs = async (warehouseId = null) => {
    try {
      const endpoint = warehouseId 
        ? `/api/hubs/warehouse/${warehouseId}`
        : '/api/hubs';
      const result = await api.get(endpoint);
      // Removed console.log to prevent console spam
      return result;
    } catch (err) {
      console.error('Error getting hubs:', err);
      throw err;
    }
  };

  /**
   * Create a new hub
   * @param {Object} hubData - Hub data including hubId (ESP HUB_ID), name, warehouseId, position
   */
  const createHub = async (hubData) => {
    try {
      const result = await api.post('/api/hubs', {
        hubId: hubData.hubId, // ESP HUB_ID value (e.g., "Gateway_L")
        name: hubData.name || hubData.hubId, // Display name
        warehouseId: hubData.warehouseId,
        position: hubData.position,
        gpsPosition: hubData.gpsPosition || null,
        status: hubData.status || 'offline',
        config: hubData.config || {}
      });
      return result;
    } catch (err) {
      console.error('Error creating hub:', err);
      throw err;
    }
  };

  /**
   * Update a hub
   */
  const updateHub = async (hubId, hubData) => {
    try {
      const result = await api.put(`/api/hubs/${hubId}`, hubData);
      return result;
    } catch (err) {
      console.error('Error updating hub:', err);
      throw err;
    }
  };

  /**
   * Update hub position (for drag-and-drop in build mode)
   */
  const updateHubPosition = async (hubId, position) => {
    try {
      const result = await api.patch(`/api/hubs/${hubId}/position`, { position });
      return result;
    } catch (err) {
      console.error('Error updating hub position:', err);
      throw err;
    }
  };

  /**
   * Update hub display name (editable from build mode)
   * NOTE: This updates the user-friendly display name, NOT the hub_id.
   * The hub_id remains constant and is used to match incoming device data.
   */
  const updateHubName = async (hubId, name) => {
    try {
      const result = await api.patch(`/api/hubs/${hubId}/name`, { name });
      return result;
    } catch (err) {
      console.error('Error updating hub name:', err);
      throw err;
    }
  };

  /**
   * Delete a hub
   */
  const deleteHub = async (hubId) => {
    try {
      await api.delete(`/api/hubs/${hubId}`);
      return true;
    } catch (err) {
      console.error('Error deleting hub:', err);
      throw err;
    }
  };

  /**
   * Get active hubs with their DB-stored positions
   * Positions come from the database only, never from devices
   */
  const getActiveHubsWithPositions = async (warehouseId = null) => {
    try {
      const endpoint = warehouseId 
        ? `/api/hubs/active-with-positions?warehouseId=${warehouseId}`
        : '/api/hubs/active-with-positions';
      const result = await api.get(endpoint);
      return result;
    } catch (err) {
      console.error('Error getting active hubs:', err);
      throw err;
    }
  };

  // Legacy aliases for backward compatibility
  const getActiveBeaconsWithPositions = getActiveHubsWithPositions;

  /**
   * Get triangulated beacon positions
   * Returns beacons with calculated positions based on trilateration from hub readings
   * @param {string|null} warehouseId - Optional warehouse ID to filter
   */
  const getTriangulatedBeacons = async (warehouseId = null) => {
    try {
      const endpoint = warehouseId 
        ? `/api/hubs/triangulated-beacons?warehouseId=${warehouseId}`
        : '/api/hubs/triangulated-beacons';
      const result = await api.get(endpoint);
      return result;
    } catch (err) {
      console.error('Error getting triangulated beacons:', err);
      throw err;
    }
  };

  /**
   * Get all known BLE beacons seen in last 24 hours
   */
  const getKnownBeacons = async (warehouseId = null) => {
    try {
      const endpoint = warehouseId 
        ? `/api/hubs/known-beacons?warehouseId=${warehouseId}`
        : '/api/hubs/known-beacons';
      const result = await api.get(endpoint);
      return result;
    } catch (err) {
      console.error('Error getting known beacons:', err);
      throw err;
    }
  };

  /**
   * Simulate a beacon for testing triangulation
   * Creates readings from all hubs in the warehouse
   */
  const simulateBeacon = async (warehouseId, bleTagId = null) => {
    try {
      const result = await api.post('/api/hubs/simulate-beacon', {
        warehouseId,
        bleTagId
      });
      console.log('🧪 Simulated beacon:', result);
      return result;
    } catch (err) {
      console.error('Error simulating beacon:', err);
      throw err;
    }
  };

  /**
   * Clear all test beacons
   */
  const clearTestBeacons = async () => {
    try {
      const result = await api.delete('/api/hubs/test-beacons');
      console.log('🧹 Cleared test beacons:', result);
      return result;
    } catch (err) {
      console.error('Error clearing test beacons:', err);
      throw err;
    }
  };

  // --- Repair Actions ---

  const createRepair = async (repairData) => {
    try {
      const result = await api.post('/api/repairs', {
        assetId: repairData.assetId,
        title: repairData.title,
        description: repairData.description || null,
        priority: repairData.priority || 'Medium',
        status: repairData.status || 'Needs Approval',
        vendorId: repairData.vendorId || null,
        notes: repairData.notes || null,
        totalRepairCost: repairData.totalRepairCost || null,
        damageDetails: repairData.damageDetails || null,
        companyId: repairData.companyId || null
      });

      await fetchData();
      return result;
    } catch (err) {
      console.error('Error creating repair:', err);
      throw err;
    }
  };

  const updateRepair = async (repairId, repairData) => {
    try {
      const result = await api.put(`/api/repairs/${repairId}`, {
        title: repairData.title,
        description: repairData.description,
        priority: repairData.priority,
        status: repairData.status,
        vendorId: repairData.vendorId,
        notes: repairData.notes,
        totalRepairCost: repairData.totalRepairCost,
        damageDetails: repairData.damageDetails,
        statusHistory: repairData.statusHistory
      });

      await fetchData();
      return result;
    } catch (err) {
      console.error('Error updating repair:', err);
      throw err;
    }
  };

  const deleteRepair = async (repairId) => {
    try {
      await api.delete(`/api/repairs/${repairId}`);
      await fetchData();
      return true;
    } catch (err) {
      console.error('Error deleting repair:', err);
      throw err;
    }
  };

  const archiveRepair = async (repairId) => {
    try {
      const result = await api.post(`/api/repairs/${repairId}/archive`);
      await fetchData();
      return result;
    } catch (err) {
      console.error('Error archiving repair:', err);
      throw err;
    }
  };

  const unarchiveRepair = async (repairId) => {
    try {
      const result = await api.post(`/api/repairs/${repairId}/unarchive`);
      await fetchData();
      return result;
    } catch (err) {
      console.error('Error unarchiving repair:', err);
      throw err;
    }
  };

  // --- Repair Comments Actions ---

  const getRepairComments = async (repairId) => {
    try {
      const result = await api.get(`/api/repairs/${repairId}/comments`);
      return result;
    } catch (err) {
      console.error('Error getting repair comments:', err);
      throw err;
    }
  };

  const addRepairComment = async (repairId, commentText, attachments = []) => {
    try {
      const result = await api.post(`/api/repairs/${repairId}/comments`, {
        commentText,
        attachments
      });
      return result;
    } catch (err) {
      console.error('Error adding repair comment:', err);
      throw err;
    }
  };

  const deleteRepairComment = async (repairId, commentId) => {
    try {
      await api.delete(`/api/repairs/${repairId}/comments/${commentId}`);
      return true;
    } catch (err) {
      console.error('Error deleting repair comment:', err);
      throw err;
    }
  };

  // --- Message Actions (Chat System) ---

  const getAssetMessages = async (assetId) => {
    try {
      const result = await api.get(`/api/messages/asset/${assetId}`);
      return result;
    } catch (err) {
      // Silently handle errors to prevent console spam
      throw err;
    }
  };

  const getRepairMessages = async (repairId) => {
    try {
      const result = await api.get(`/api/messages/repair/${repairId}`);
      return result;
    } catch (err) {
      // Silently handle errors to prevent console spam
      throw err;
    }
  };

  const sendMessage = async (messageData) => {
    try {
      const result = await api.post('/api/messages', {
        assetId: messageData.assetId || null,
        repairId: messageData.repairId || null,
        messageText: messageData.messageText,
        attachments: messageData.attachments || []
      });
      return result;
    } catch (err) {
      console.error('Error sending message:', err);
      throw err;
    }
  };

  const getUnreadMessageCount = async () => {
    try {
      const result = await api.get('/api/messages/unread');
      return result;
    } catch (err) {
      console.error('Error getting unread count:', err);
      return { totalUnread: 0, assetUnread: 0, repairUnread: 0 };
    }
  };

  const getAssetUnreadCount = async (assetId) => {
    try {
      const result = await api.get(`/api/messages/unread/asset/${assetId}`);
      return result.unread || 0;
    } catch (err) {
      console.error('Error getting asset unread count:', err);
      return 0;
    }
  };

  const getRepairUnreadCount = async (repairId) => {
    try {
      const result = await api.get(`/api/messages/unread/repair/${repairId}`);
      return result.unread || 0;
    } catch (err) {
      console.error('Error getting repair unread count:', err);
      return 0;
    }
  };

  const markMessageRead = async (messageId) => {
    try {
      const result = await api.patch(`/api/messages/${messageId}/read`);
      return result;
    } catch (err) {
      console.error('Error marking message as read:', err);
      throw err;
    }
  };

  const deleteMessage = async (messageId) => {
    try {
      await api.delete(`/api/messages/${messageId}`);
      return true;
    } catch (err) {
      console.error('Error deleting message:', err);
      throw err;
    }
  };

  // --- Inspection Actions ---

  const getInspections = async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.assetId) params.append('assetId', filters.assetId);
      if (filters.inspectionType) params.append('inspectionType', filters.inspectionType);
      if (filters.status) params.append('status', filters.status);
      if (filters.requiresApproval) params.append('requiresApproval', filters.requiresApproval);
      
      const queryString = params.toString();
      const url = `/api/inspections${queryString ? '?' + queryString : ''}`;
      const result = await api.get(url);
      return result;
    } catch (err) {
      console.error('Error getting inspections:', err);
      throw err;
    }
  };

  const getInspection = async (inspectionId) => {
    try {
      const result = await api.get(`/api/inspections/${inspectionId}`);
      return result;
    } catch (err) {
      console.error('Error getting inspection:', err);
      throw err;
    }
  };

  const getAssetInspections = async (assetId) => {
    try {
      const result = await api.get(`/api/inspections/asset/${assetId}`);
      return result;
    } catch (err) {
      console.error('Error getting asset inspections:', err);
      throw err;
    }
  };

  const getPendingApprovals = useCallback(async () => {
    try {
      // If no auth token, skip remote call and return empty list to avoid 401 noise
      if (!api.hasToken()) return [];
      const result = await api.get('/api/inspections/pending-approval');
      return result;
    } catch (err) {
      // Silently return empty array for auth errors to prevent console spam
      if (err?.status === 401 || err?.status === 403) {
        return [];
      }
      console.error('Error getting pending approvals:', err);
      return []; // Return empty array instead of throwing to prevent infinite loops
    }
  }, []);

  const createInspection = async (inspectionData) => {
    try {
      const result = await api.post('/api/inspections', inspectionData);
      return result;
    } catch (err) {
      console.error('Error creating inspection:', err);
      throw err;
    }
  };

  const updateInspection = async (inspectionId, inspectionData) => {
    try {
      const result = await api.put(`/api/inspections/${inspectionId}`, inspectionData);
      return result;
    } catch (err) {
      console.error('Error updating inspection:', err);
      throw err;
    }
  };

  const approveInspection = async (inspectionId, action, vendorId = null) => {
    try {
      const result = await api.post(`/api/inspections/${inspectionId}/approve`, {
        action,
        vendorId
      });
      return result;
    } catch (err) {
      console.error('Error approving inspection:', err);
      throw err;
    }
  };

  const getAssetHistory = async (assetId) => {
    try {
      const result = await api.get(`/api/assets/${assetId}/history`);
      return result;
    } catch (err) {
      console.error('Error getting asset history:', err);
      // Return empty array if endpoint doesn't exist yet
      return [];
    }
  };

  // --- Asset Route Actions (for Last Location Replay) ---

  const getAssetRoute = async (assetId) => {
    try {
      const result = await api.get(`/api/assets/${assetId}/route`);
      return result;
    } catch (err) {
      console.error('Error getting asset route:', err);
      return null;
    }
  };

  const saveAssetRoute = async (assetId, routeData) => {
    try {
      const result = await api.post(`/api/assets/${assetId}/route`, routeData);
      return result;
    } catch (err) {
      console.error('Error saving asset route:', err);
      throw err;
    }
  };

  const updateAssetRoute = async (routeId, routeData) => {
    try {
      const result = await api.put(`/api/routes/${routeId}`, routeData);
      return result;
    } catch (err) {
      console.error('Error updating asset route:', err);
      throw err;
    }
  };

  const deleteAssetRoute = async (routeId) => {
    try {
      await api.delete(`/api/routes/${routeId}`);
      return true;
    } catch (err) {
      console.error('Error deleting asset route:', err);
      throw err;
    }
  };

  const getAssetLocationHistory = async (assetId, filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.startTime) params.append('startTime', filters.startTime);
      if (filters.endTime) params.append('endTime', filters.endTime);
      if (filters.limit) params.append('limit', filters.limit);
      
      const queryString = params.toString();
      const url = `/api/assets/${assetId}/location-history${queryString ? '?' + queryString : ''}`;
      const result = await api.get(url);
      return result;
    } catch (err) {
      console.error('Error getting asset location history:', err);
      return [];
    }
  };

  // --- User Actions ---

  const getUsers = useCallback(async () => {
    try {
      const result = await api.get('/api/users');
      setUsers(result);
      return result;
    } catch (err) {
      console.error('Error getting users:', err);
      throw err;
    }
  }, []);

  const updateUser = async (userId, userData) => {
    try {
      const result = await api.put(`/api/users/${userId}`, {
        role: userData.role,
        companyId: userData.companyId
      });
      await getUsers(); // Refresh users list
      return result;
    } catch (err) {
      console.error('Error updating user:', err);
      throw err;
    }
  };

  const deleteUser = async (userId) => {
    try {
      await api.delete(`/api/users/${userId}`);
      await getUsers(); // Refresh users list
      return true;
    } catch (err) {
      console.error('Error deleting user:', err);
      throw err;
    }
  };

  return {
    warehouses,
    repairs,
    organizations,
    users,
    loading,
    error,
    createWarehouse,
    checkWarehouseExists,
    updateBlueprint,
    updateZones,
    updateWarehouseGPS,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    createAsset,
    updateAsset,
    updateAssetPosition,
    updateAssetCapacity,
    scrapAsset,
    addAssetImages,
    removeAssetImage,
    deleteAsset,
    assignFiducialMarker,
    releaseFiducialMarker,
    getBeacons,
    createBeacon,
    updateBeacon,
    updateBeaconPosition,
    deleteBeacon,
    // Hub actions (ESP32 gateway devices)
    getHubs,
    createHub,
    updateHub,
    updateHubPosition,
    updateHubName,
    deleteHub,
    getActiveHubsWithPositions,
    getActiveBeaconsWithPositions, // Legacy alias
    getTriangulatedBeacons,
    getKnownBeacons,
    simulateBeacon,
    clearTestBeacons,
    createRepair,
    updateRepair,
    deleteRepair,
    archiveRepair,
    unarchiveRepair,
    getRepairComments,
    addRepairComment,
    deleteRepairComment,
    // Message actions (Chat System)
    getAssetMessages,
    getRepairMessages,
    sendMessage,
    getUnreadMessageCount,
    getAssetUnreadCount,
    getRepairUnreadCount,
    markMessageRead,
    deleteMessage,
    getUsers,
    updateUser,
    deleteUser,
    // Inspection actions
    getInspections,
    // Asset Route actions (for Last Location Replay)
    getAssetRoute,
    saveAssetRoute,
    updateAssetRoute,
    deleteAssetRoute,
    getAssetLocationHistory,
    getInspection,
    getAssetInspections,
    getPendingApprovals,
    createInspection,
    updateInspection,
    approveInspection,
    getAssetHistory,
    refresh: fetchData
  };
}

export default useApi;

/* ── Delivery & Fulfillment API helpers ─────────────────────────────────── */
// These are standalone async functions (not part of the hook) so they can be
// called from any component via the shared `api` lib directly.
// The hook exposes them via the return object below if needed in the future.

export async function getDeliveryShipments(orgId) {
  const res = await fetch(`/api/delivery/shipments${orgId ? `?orgId=${orgId}` : ''}`, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to fetch delivery shipments');
  return res.json();
}

export async function importDeliveryShipments(records, fileName) {
  const res = await fetch('/api/delivery/shipments/import', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ records, fileName }),
  });
  if (!res.ok) throw new Error('Failed to import delivery shipments');
  return res.json();
}

export async function updateDeliveryStatus(shipmentId, status) {
  const res = await fetch(`/api/delivery/shipments/${shipmentId}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw new Error('Failed to update delivery status');
  return res.json();
}

export async function getDeliveryLabels(shipmentId, type = 'box') {
  const res = await fetch(`/api/delivery/shipments/${shipmentId}/labels?type=${type}`, {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });
  if (!res.ok) throw new Error('Failed to fetch delivery labels');
  return res.json();
}

